// ============================================================================
// Schedule identity colours
// ============================================================================
//
// One colour per schedule (shift set), derived from its id alone, so the week
// view's card border and the schedule list's dot always agree without either
// side knowing about the other, and a company can have as many schedules as it
// likes without falling into a fixed palette's "mod N" repeat.
//
// How the colour is chosen, in OKLCH (perceptual lightness, chroma, hue):
// - The id drives two independent walks at once, hue and lightness, using the
//   R2 sequence (steps 1/p and 1/p^2, p = 1.3247..., the plastic number). It is
//   the 2D counterpart of the golden-ratio walk: id k and id k+g end up the same
//   distance apart in (hue, lightness) whatever k is, and that distance stays
//   large for small gaps g. A 1D hue walk with lightness picked by id mod 3
//   cannot do this: every 1D step has gaps (its continued-fraction
//   denominators) where the hue nearly repeats, and when such a gap is a
//   multiple of 3 the lightness repeats too. The previous 1/phi^3 step did
//   exactly that at gap 72 (ids 1 and 73 were both #ab6900).
// - Lightness is continuous (0.50-0.64), not three bands, so rounding to hex
//   never folds two ids onto the same value: ids 1..1000 are 1000 distinct hex
//   strings.
// - What this does NOT promise: that any two of an unlimited number of
//   schedules look different. The usable colour region (the lightness range
//   below, one chroma, about 256 degrees of hue) holds only about 40 colours
//   that are all a clear OKLab deltaE 0.05 apart, so past a few dozen
//   schedules some pairs are inevitably close. Measured on this code: no gap
//   between ids below 49 averages under deltaE 0.05, and about 1.3% of all
//   pairs among ids 1..1000 fall under the 0.02 just-noticeable difference
//   (2.1% with the old walk).
// - Two hue arcs are never used: red (4-46 degrees, the error/destructive
//   colour) and green (118-180 degrees, the success/active badge), so a
//   schedule's colour cannot be mistaken for a status on the same screen.
// - Lightness stays at or below 0.64, which keeps each colour at >= 3:1
//   against the white card surface (WCAG 1.4.11 for a 4px border or a 12-16px
//   dot). Colours outside the sRGB gamut are brought in by lowering chroma,
//   never by shifting hue or lightness, so the walk stays as designed.
//
// The result is always a 6-digit hex string ("#rrggbb"), so callers may append
// a 2-digit alpha (`color + '20'`) to build a tint.

/** Colour for a shift that belongs to no schedule (no set id, no rule id). */
export const SCHEDULE_COLOR_NONE = '#6B7280';

// The plastic number p, the real root of x^3 = x + 1. Hue advances 1/p of the
// usable arc per id and lightness 1/p^2 of its range per id.
const PLASTIC = 1.324717957244746;
const HUE_STEP = 1 / PLASTIC;
const LIGHTNESS_STEP = 1 / (PLASTIC * PLASTIC);
const HUE_OFFSET = 0.95;

const LIGHTNESS_MIN = 0.5;
const LIGHTNESS_MAX = 0.64;
const CHROMA = 0.15;

// Usable hue arcs in degrees (end exclusive), skipping red and green.
const HUE_ARCS: ReadonlyArray<readonly [number, number]> = [
  [46, 118],
  [180, 364], // wraps through 360 to 4
];
const HUE_SPAN = HUE_ARCS.reduce((sum, [from, to]) => sum + (to - from), 0);

function frac(x: number): number {
  return x - Math.floor(x);
}

function hueAt(t: number): number {
  let x = t * HUE_SPAN;
  for (const [from, to] of HUE_ARCS) {
    if (x < to - from) return (from + x) % 360;
    x -= to - from;
  }
  return HUE_ARCS[0][0];
}

function oklabToLinearSrgb(L: number, a: number, b: number): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

function inGamut(rgb: [number, number, number]): boolean {
  return rgb.every((c) => c >= -1e-6 && c <= 1 + 1e-6);
}

function toHexChannel(linear: number): string {
  const c = Math.min(1, Math.max(0, linear));
  const srgb = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
  return Math.round(srgb * 255).toString(16).padStart(2, '0');
}

/** OKLCH to "#rrggbb", reducing chroma (not hue) until the colour fits sRGB. */
function oklchToHex(L: number, C: number, hueDeg: number): string {
  const h = (hueDeg * Math.PI) / 180;
  const at = (c: number) => oklabToLinearSrgb(L, c * Math.cos(h), c * Math.sin(h));
  let chroma = C;
  if (!inGamut(at(C))) {
    let lo = 0;
    let hi = C;
    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(at(mid))) lo = mid;
      else hi = mid;
    }
    chroma = lo;
  }
  const [r, g, b] = at(chroma);
  return `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;
}

const cache = new Map<number, string>();

/**
 * The colour of the schedule with this id. Deterministic and stable: the same
 * id gives the same colour on every page, every session and every device.
 */
export function getScheduleColor(id: number): string {
  if (!Number.isFinite(id)) return SCHEDULE_COLOR_NONE;
  const key = Math.trunc(id);
  const hit = cache.get(key);
  if (hit) return hit;
  const lightness = LIGHTNESS_MIN + (LIGHTNESS_MAX - LIGHTNESS_MIN) * frac(key * LIGHTNESS_STEP);
  const color = oklchToHex(lightness, CHROMA, hueAt(frac(key * HUE_STEP + HUE_OFFSET)));
  cache.set(key, color);
  return color;
}
